import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontalIcon, PlusIcon, TicketIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatMoney } from '@ems/ui/lib/format'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { moduleAccess } from '#admin/features/tenant/api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import {
  capacityLabel,
  categoryLabel,
  salesWindowLabel,
  type TicketType,
} from '#admin/features/ticket-types/api.ts'
import {
  useDeleteTicketType,
  useReorderTicketTypes,
  useSetTicketTypeActive,
} from '#admin/features/ticket-types/mutations.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import { TicketTypeDialog } from '#admin/features/ticket-types/ticket-type-dialog.tsx'
import { TicketTypesSkeleton } from '#admin/features/ticket-types/ticket-types-skeleton.tsx'

/**
 * `/ticketing/ticket-types` — what this edition sells.
 *
 * Beside the registration form because both are set up before sales open. One flat list: there is
 * no activity entity (ADMIN_CATALOG_PLAN.md §5.1), so a workshop is its own type, told apart by
 * category.
 *
 * Guarded on `ticket.view`; without `ticket.manage` the write controls are removed and the notice
 * says who to ask, as on the registration form.
 */
export const Route = createFileRoute('/_authenticated/ticketing/ticket-types')({
  pendingComponent: TicketTypesSkeleton,
  staticData: { crumb: { parents: [{ label: 'Ticketing' }], label: 'Ticket types' } },
  loader: async ({ context }) => {
    const [session, manifest] = await Promise.all([
      context.queryClient.ensureQueryData(sessionQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ])
    if (manifest === null || !can(session, 'ticket.view')) throw notFound()
    await context.queryClient.ensureQueryData(ticketTypesQuery())
  },
  notFoundComponent: NotFoundView,
  component: TicketTypesPage,
})

type Editing = { type: TicketType | null } | null

function TicketTypesPage() {
  const { data } = useSuspenseQuery(ticketTypesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  // The loader refused a null manifest; the fallback only satisfies the type.
  const zone = manifest?.tenant.timezone ?? 'UTC'
  const canManage = can(session, 'ticket.manage')

  const [editing, setEditing] = useState<Editing>(null)
  const [deleting, setDeleting] = useState<TicketType | null>(null)
  const { mutate: toggleActive, isPending: toggling } = useSetTicketTypeActive()
  const remove = useDeleteTicketType(() => setDeleting(null))
  const { mutate: reorder, isPending: reordering } = useReorderTicketTypes()

  const active = data.items.filter((type) => type.isActive).length

  const columns = useMemo<ColumnDef<TicketType, never>[]>(() => {
    // One clock per render, so every row's window is judged at the same instant.
    const now = new Date()
    return [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      { accessorKey: 'name', header: 'Name', meta: { primary: true } },
      {
        id: 'category',
        header: 'Category',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{categoryLabel(row.original.category)}</span>
        ),
      },
      {
        id: 'price',
        header: 'Price',
        meta: { align: 'end' },
        cell: ({ row }) =>
          row.original.price === 0
            ? 'Free'
            : formatMoney(row.original.price, row.original.currency),
      },
      {
        id: 'seats',
        header: 'Seats',
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">{capacityLabel(row.original)}</span>
        ),
      },
      {
        id: 'sales',
        header: 'Sales window',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {salesWindowLabel(row.original, now, zone)}
          </span>
        ),
      },
      {
        id: 'active',
        header: 'State',
        cell: ({ row }) =>
          row.original.isActive ? (
            <StatusBadge tone="success">active</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">inactive</StatusBadge>
          ),
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const },
              cell: ({ row }: { row: { original: TicketType } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${row.original.name}`}
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing({ type: row.original })}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={toggling}
                      onSelect={() =>
                        toggleActive({ type: row.original, isActive: !row.original.isActive })
                      }
                    >
                      {row.original.isActive ? 'Deactivate' : 'Activate'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(row.original)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies ColumnDef<TicketType, never>,
          ]
        : []),
    ]
  }, [canManage, toggleActive, toggling, zone])

  // The loader refused a null manifest; this narrows it for the dialog.
  if (manifest === null) return null

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Ticket types"
        description={`What this edition sells · ${active} of ${data.items.length} active`}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ type: null })}>
              <PlusIcon />
              Add ticket type
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the ticket types" />}

      <DataTable
        caption="Ticket types"
        columns={columns}
        data={data.items}
        getRowId={(type) => type.id}
        // The list is in this order everywhere a type is offered, so dragging a row sets the order.
        reorder={
          canManage
            ? { onReorder: reorder, label: (type) => type.name, disabled: reordering }
            : undefined
        }
        empty={
          <EmptyState
            icon={<TicketIcon />}
            title="No ticket types yet"
            description="Nobody can register until there is something to register for. Add the first type."
          />
        }
      />

      {editing === null ? null : (
        <TicketTypeDialog
          // Keyed, so opening another type starts from its own values rather than the last edit.
          key={editing.type?.id ?? 'new'}
          type={editing.type}
          currency={manifest.tenant.currencyCode}
          nextSortOrder={Math.max(-1, ...data.items.map((type) => type.sortOrder)) + 1}
          zone={manifest.tenant.timezone}
          billingOff={moduleAccess(manifest, 'billing') === 'none'}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={`Delete “${deleting?.name ?? ''}”?`}
        description="It disappears from every list. A type that has registrations can’t be deleted; deactivate it instead."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => (deleting === null ? undefined : remove.mutate(deleting))}
      />
    </div>
  )
}
